/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, isEqual } from 'lodash';
import moment from 'moment';
// @ts-ignore
import Swiper from 'react-id-swiper';
import 'url-search-params-polyfill';

import CostExplanationBar from './costexplanationbar';
import RoleMainCard from './rolecard/RoleMainCard';
import RoleDetailCard from './rolecard/RoleDetailCard';
import TimeDateCard from './rolecard/TimeDateCard';
import { LocationCard } from './locationcard/LocationCard';
import { ExtraInfoCard } from './extrainfocard';
import SummaryBar from './summarybar/PostJobSummaryBar';
import PaymentModal from './paymentmodal';
import { checkFieldsAreNotEmpty, checkFieldsValidation } from './utils';
import BookingsFeedbackModal from '../common/bookings-feedback-modal/BookingsFeedbackModal';
import { ErrorMessage } from '../common';
import Step from '../common/step';
import { minStartTimeAsync, sendBookingRequestAsync, updateField, updateRoleExtra } from '../../actions/BookingRequest';
import { fetchClientAddressListAsync } from '../../actions/ClientData';
import { toggleModal, toggleOptionCard } from '../../actions/ViewController';
import { getTime } from '../../api/DataHandlers';
import { jobModes, modalTypes } from '../../consts';
import history from '../../history';
import { usePrevious } from '../../hooks';
import { TRootState } from '../../reducers';
import { TServiceKey } from '../../types';
import { getErrorMessages } from '../../utils';

type TPostJobContainerProps = {
    mode: 'postjob' | 'checkout';
}

export default function PostJobContainer({
    mode,
}: TPostJobContainerProps) {
    const dispatch = useDispatch();

    const [error, setError] = useState(null);

    const swiperRef = useRef(null);

    const titleRef = useRef(null);
    const freelancersInformationRef = useRef(null);
    const natureRef = useRef(null);
    const qualificationsRef = useRef(null);
    const risksRef = useRef(null);
    const facilitiesRef = useRef(null);
    const payableRef = useRef(null);

    const titleLabelRef = useRef(null);
    const freelancersInformationLabelRef = useRef(null);
    const natureLabelRef = useRef(null);
    const qualificationsLabelRef = useRef(null);
    const risksLabelRef = useRef(null);
    const facilitiesLabelRef = useRef(null);
    const payableLabelRef = useRef(null);

    const {
        bookingRequest,
        clientData,
        minStart,
        paygrade,
        paymentType,
        roleData,
        rolePopout,
        servicesStructure,
        user,
    } = useSelector((state: TRootState) => ({
        bookingRequest: state.requestData.bookingRequest,
        clientData: state.clientData,
        minStart: state.requestData.minStart,
        paygrade: state.requestData.paygrade,
        paymentType: state.view.modals.paymentModal,
        roleData: state.requestData.roleData,
        rolePopout: state.view.rolePopout,
        servicesStructure: state.servicesStructure,
        user: state.user,
    }));

    const [defaultQualifications, setDefaultQualifications] = useState(
        clientData.basicInfo.default_required_qualifications
    );
    const [defaultRisks, setDefaultRisks] = useState(clientData.basicInfo.default_safety_risks);
    const [defaultFacilities, setDefaultFacilities] = useState(clientData.basicInfo.default_available_facilities);
    const [defaultPayable, setDefaultPayable] = useState(clientData.basicInfo.default_expenses_payable);

    const [qualificationsChanged, setQualificationsChanged] = useState(false);
    const [risksChanged, setRisksChanged] = useState(false);
    const [facilitiesChanged, setFacilitiesChanged] = useState(false);
    const [payableChanged, setPayableChanged] = useState(false);

    const prevMinStart = usePrevious(minStart);
    const prevPaygrade = usePrevious(paygrade);
    const prevRoleData = usePrevious(roleData);
    const prevService = usePrevious(bookingRequest.service);

    const clearFields = () => {
        dispatch(fetchClientAddressListAsync(clientData.basicInfo.id));
        dispatch(updateField('address1', null));
        dispatch(updateField('address2', null));
        dispatch(updateField('city', null));
        dispatch(updateField('postcode', null));
        dispatch(updateField('date', null));
        dispatch(updateField('start_time', null));
        dispatch(toggleModal('paymentModal', null));
    };

    useEffect(() => {
        const currentUrlParams = new URLSearchParams(history.location.search);
        const service = currentUrlParams.get('service');

        if (service && servicesStructure) {
            if (Object.keys(servicesStructure).filter((s: TServiceKey) => !!s).includes(service)) {
                dispatch(updateField('service', service));

                // @ts-ignore
                const { role_field_name } = servicesStructure[service];
                const role = currentUrlParams.get(role_field_name);

                // @ts-ignore
                if (role && Object.keys(servicesStructure[service][role_field_name]).includes(role)) {
                    dispatch(updateRoleExtra(service, role_field_name, role));
                }

                setTimeout(() => dispatch(toggleOptionCard('role')), 300);
            }
        }

        dispatch(minStartTimeAsync(bookingRequest.service));

        if (defaultQualifications) setQualificationsChanged(true);
        if (defaultRisks) setRisksChanged(true);
        if (defaultFacilities) setFacilitiesChanged(true);
        if (defaultPayable) setPayableChanged(true);

        // @ts-ignore
        if (['checkout', 'signup'].includes(history.location.state?.mode)) {
            Function.prototype();
        } else if (currentUrlParams.has('createafter')) {
            const startDate = moment(currentUrlParams.get('createafter'));
            const endDate = moment(currentUrlParams.get('createbefore'));
            const difference = endDate.diff(startDate, 'hours', true);
            const duration = difference > 4 ? difference : 4;

            dispatch(updateField('date', startDate.toISOString().slice(0, 10)));
            dispatch(updateField('start_time', startDate.format('HH:mm')));
            dispatch(updateField('duration', duration));
        } else {
            clearFields();
        }

        if (!clientData.loggedIn) {
            dispatch(toggleModal('authModal', modalTypes.SIGNUP));
        } else if (!(user.isAgent && user.agency.features.includes('booking_tunnel')) && !user.isClient) {
            history.push('/jobs/list/');
        }
    }, []);

    useEffect(() => {
        if (
            bookingRequest.service && (
                prevService !== bookingRequest.service
                // eslint-disable-next-line eqeqeq
                || prevRoleData.driver.vehicle_type != roleData.driver.vehicle_type
                || prevRoleData.hospitality.role !== roleData.hospitality.role
                || prevRoleData.industry.role !== roleData.industry.role
                || prevRoleData.construction.role !== roleData.construction.role
                || prevRoleData.office.role !== roleData.office.role
            )
        ) {
            
            // @ts-ignore
            const roleField = servicesStructure[bookingRequest.service].role_field_name;
            const roleValue = roleData[bookingRequest.service][roleField];
            const payload = {
                service: bookingRequest.service,
                [roleField]: roleValue
            };
            dispatch(minStartTimeAsync(payload));
        }
    }, [bookingRequest.service, roleData.construction?.role, roleData.driver?.vehicle_type, roleData.hospitality?.role,
        roleData.industry?.role, roleData.office?.role]);

    useEffect(() => {
        if ((minStart && prevMinStart !== minStart) || !bookingRequest.start_time) {
            const { date, start_time } = bookingRequest;

            if (!start_time || moment(`${date} ${start_time}`).isBefore(minStart)) {
                dispatch(updateField('start_time', getTime(minStart)));
            }
        }
    }, [bookingRequest.date, bookingRequest.start_time, minStart]);

    useEffect(() => {
        if (!isEmpty(paygrade) && !isEqual(paygrade, prevPaygrade)) {
            if (!qualificationsChanged) setDefaultQualifications(paygrade.default_required_qualifications);
            if (!risksChanged) setDefaultRisks(paygrade.default_safety_risks);
            if (!facilitiesChanged) setDefaultFacilities(paygrade.default_available_facilities);
            if (!payableChanged) setDefaultPayable(paygrade.default_expenses_payable);
        }
    }, [paygrade]);

    const handleClickOverlay = () => {
        dispatch(toggleOptionCard());
    };

    const handleClickBooking = () => {
        const fields = [
            payableRef,
            facilitiesRef,
            risksRef,
            qualificationsRef,
            natureRef,
            freelancersInformationRef,
            titleRef,
        ];

        const labels = [
            payableLabelRef,
            facilitiesLabelRef,
            risksLabelRef,
            qualificationsLabelRef,
            natureLabelRef,
            freelancersInformationLabelRef,
            titleLabelRef,
        ];

        const { isMultiple, service } = bookingRequest;

        const requestData = {
            service,
            ...bookingRequest,
        };

        if (isMultiple) delete requestData.date;
        else delete requestData.dates;

        let sendableRequest = JSON.parse(JSON.stringify(requestData));
        sendableRequest = {
            ...sendableRequest,
            ...roleData[service],
        };

        setError(null);

        checkFieldsValidation(fields, labels);

        if (checkFieldsAreNotEmpty(fields)) {
            dispatch(sendBookingRequestAsync(
                sendableRequest,
                (response: any, err: any) => {
                    if (response && !err) {
                        const jobs = [].concat(response.content);
                        const modalType = 'card';
                        dispatch(toggleModal('paymentModal', modalType));
                        history.push({
                            pathname: `/job/checkout/${jobs[0].id}`,
                            state: { mode: 'checkout' },
                        });
                    } else if (err) {
                        setError(getErrorMessages(err));
                    }
                },
            ));
        }
    };

    const handleSwipe = (index: number) => {
        swiperRef.current.swiper.slideTo(index);
    };

    const params = {
        nextButton: '.swiper-button-next',
        prevButton: '.swiper-button-prev',
        spaceBetween: 20,
        containerClass: 'cards-container',
        runCallbacksOnInit: true,
    };

    const { roleExtras, shiftTimes } = rolePopout;
    const cardsContainerClassName = `cards-container${roleExtras || shiftTimes ? ' cards-container-expanded' : ''}`;

    return (
        <div>
            <BookingsFeedbackModal />
            <div className="page page--postjob visible-xs">
                <Step />
                <div className="cards-relative-container">
                    <Swiper
                        {...params}
                        ref={swiperRef}
                    >
                        <RoleMainCard
                            className="swiper-slide"
                            service_key={bookingRequest.service}
                            date={bookingRequest.date}
                            dates={bookingRequest.dates}
                            number_of_freelancers={bookingRequest.number_of_freelancers}
                            isMultiple={bookingRequest.isMultiple}
                            updateField={updateField}
                            mode={jobModes.CREATE}
                            handleSwipe={handleSwipe}
                        />
                        <RoleDetailCard
                            className="swiper-slide"
                            service_key={bookingRequest.service}
                            roleData={roleData}
                        />
                        <TimeDateCard
                            className="swiper-slide"
                            calendarOnly={false}
                            updateField={updateField}
                        />
                        <LocationCard
                            className="swiper-slide"
                            address1={bookingRequest.address1}
                            address2={bookingRequest.address2}
                            city={bookingRequest.city}
                            postcode={bookingRequest.postcode}
                            updateField={(field, data) => dispatch(updateField(field, data))}
                            inputOnly={false}
                            mode={jobModes.CREATE}
                        />
                        <ExtraInfoCard
                            updateField={updateField}
                            className="swiper-slide"
                            freelancers_information={bookingRequest.freelancers_information}
                            admin_information={bookingRequest.admin_information}
                            nature_of_business={bookingRequest.nature_of_business}
                            required_qualifications={bookingRequest.required_qualifications}
                            safety_risks={bookingRequest.safety_risks}
                            available_facilities={bookingRequest.available_facilities}
                            expenses_payable={bookingRequest.expenses_payable}
                            title={bookingRequest.title}
                            default_nature_of_business={clientData.basicInfo.default_nature_of_business}
                            default_required_qualifications={defaultQualifications}
                            default_safety_risks={defaultRisks}
                            default_available_facilities={defaultFacilities}
                            default_expenses_payable={defaultPayable}
                            freelancersInformationRef={freelancersInformationRef}
                            natureRef={natureRef}
                            qualificationsRef={qualificationsRef}
                            risksRef={risksRef}
                            facilitiesRef={facilitiesRef}
                            payableRef={payableRef}
                            titleRef={titleRef}
                            freelancersInformationLabelRef={freelancersInformationLabelRef}
                            natureLabelRef={natureLabelRef}
                            qualificationsLabelRef={qualificationsLabelRef}
                            risksLabelRef={risksLabelRef}
                            facilitiesLabelRef={facilitiesLabelRef}
                            payableLabelRef={payableLabelRef}
                            titleLabelRef={titleLabelRef}
                            isClient={true}
                            qualificationsChanged={qualificationsChanged}
                            risksChanged={risksChanged}
                            facilitiesChanged={facilitiesChanged}
                            payableChanged={payableChanged}
                            setQualificationsChanged={setQualificationsChanged}
                            setRisksChanged={setRisksChanged}
                            setFacilitiesChanged={setFacilitiesChanged}
                            setPayableChanged={setPayableChanged}
                        />
                    </Swiper>
                </div>
                {error && (
                    <ErrorMessage message={error} />
                )}
                {(mode === 'postjob' || paygrade.client_pay_per_hour) && (
                    <>
                        <SummaryBar
                            mode={mode}
                            onClickBooking={handleClickBooking}
                        />
                        <CostExplanationBar />
                    </>
                )}
            </div>
            <div className="page page--postjob hidden-xs">
                <h1>Create a booking</h1>
                <Step />
                <div className={cardsContainerClassName}>
                    <div className={`card-wrapper${roleExtras || shiftTimes ? ' expanded' : ''}`}>
                        <RoleMainCard
                            service_key={bookingRequest.service}
                            date={bookingRequest.date}
                            dates={bookingRequest.dates}
                            isMultiple={bookingRequest.isMultiple}
                            number_of_freelancers={bookingRequest.number_of_freelancers}
                            mode={jobModes.CREATE}
                            updateField={updateField}
                        />
                        {roleExtras && (
                            <RoleDetailCard
                                service_key={bookingRequest.service}
                                roleData={roleData}
                                displayRoleIcon={false}
                            />
                        )}
                        {shiftTimes && (
                            <TimeDateCard
                                calendarOnly={false}
                                updateField={updateField}
                            />
                        )}
                    </div>
                    <LocationCard
                        address1={bookingRequest.address1}
                        address2={bookingRequest.address2}
                        city={bookingRequest.city}
                        postcode={bookingRequest.postcode}
                        updateField={(field, data) => dispatch(updateField(field, data))}
                        inputOnly={false}
                        mode={jobModes.CREATE}
                    />
                    <ExtraInfoCard
                        updateField={updateField}
                        freelancers_information={bookingRequest.freelancers_information}
                        admin_information={bookingRequest.admin_information}
                        nature_of_business={bookingRequest.nature_of_business}
                        required_qualifications={bookingRequest.required_qualifications}
                        safety_risks={bookingRequest.safety_risks}
                        available_facilities={bookingRequest.available_facilities}
                        expenses_payable={bookingRequest.expenses_payable}
                        title={bookingRequest.title}
                        default_nature_of_business={clientData.basicInfo.default_nature_of_business}
                        default_required_qualifications={defaultQualifications}
                        default_safety_risks={defaultRisks}
                        default_available_facilities={defaultFacilities}
                        default_expenses_payable={defaultPayable}
                        freelancersInformationRef={freelancersInformationRef}
                        natureRef={natureRef}
                        qualificationsRef={qualificationsRef}
                        risksRef={risksRef}
                        facilitiesRef={facilitiesRef}
                        payableRef={payableRef}
                        titleRef={titleRef}
                        freelancersInformationLabelRef={freelancersInformationLabelRef}
                        natureLabelRef={natureLabelRef}
                        qualificationsLabelRef={qualificationsLabelRef}
                        risksLabelRef={risksLabelRef}
                        facilitiesLabelRef={facilitiesLabelRef}
                        payableLabelRef={payableLabelRef}
                        titleLabelRef={titleLabelRef}
                        isClient={true}
                        qualificationsChanged={qualificationsChanged}
                        risksChanged={risksChanged}
                        facilitiesChanged={facilitiesChanged}
                        payableChanged={payableChanged}
                        setQualificationsChanged={setQualificationsChanged}
                        setRisksChanged={setRisksChanged}
                        setFacilitiesChanged={setFacilitiesChanged}
                        setPayableChanged={setPayableChanged}
                    />
                </div>
                {error && (
                    <ErrorMessage message={error} />
                )}
                {(mode === 'postjob' || paygrade.client_pay_per_hour) && (
                    <>
                        <SummaryBar
                            mode={mode}
                            onClickBooking={handleClickBooking}
                        />
                        <CostExplanationBar />
                    </>
                )}
                {(roleExtras || shiftTimes) && (
                    <div
                        className="overlay-full"
                        onClick={handleClickOverlay}
                    />
                )}
            </div>
            {paymentType && <PaymentModal />}
        </div>
    );
}
